import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restaurant as KitchenIcon,
  LocalBar as BarIcon,
  PointOfSale as StationIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { stationsApi } from '../api/stations.api';
import { Station, StationType } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const emptyStation: Partial<Station> = {
  name: '',
  type: 'cocina',
  description: '',
  isActive: true,
};

const Stations: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Partial<Station>>(emptyStation);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);

  const load = useCallback(async () => {
    try {
      const res = await stationsApi.getAll();
      setStations(res.data);
    } catch (err) {
      console.error('Error loading stations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenCreate = () => {
    setSelected(emptyStation);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (s: Station) => {
    setSelected({ ...s });
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...selected,
        printerPort: selected.printerPort ? Number(selected.printerPort) : undefined,
      };
      if (isEditing && selected.id) {
        await stationsApi.update(selected.id, payload);
      } else {
        await stationsApi.create(payload);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      console.error('Error saving station:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await stationsApi.delete(deleteId);
      setDeleteDialogOpen(false);
      load();
    } catch (err) {
      console.error('Error deleting station:', err);
    }
  };

  const handleChange = (field: string, value: any) => {
    setSelected((prev) => ({ ...prev, [field]: value }));
  };

  const handleTestPrint = async (s: Station) => {
    if (!s.printerIp) {
      alert('Esta estacion no tiene impresora configurada. Edita la estacion y agrega la IP.');
      return;
    }
    try {
      const res = await stationsApi.testPrint(s.id);
      if (res.data.ok) {
        alert(`OK. Ticket de prueba enviado a ${s.printerIp}:${s.printerPort || 9100}.\nRevisa la impresora.`);
      } else {
        alert(`No se pudo imprimir: ${res.data.reason || 'desconocido'}\n\nVerifica:\n- La impresora este encendida\n- IP/puerto correctos\n- Misma red que el servidor`);
      }
    } catch (err: any) {
      alert(`Error: ${err?.message || err}`);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StationIcon sx={{ fontSize: 32, color: '#F59E0B' }} />
          <Typography variant="h4">Estaciones (Bar / Cocina)</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nueva Estacion
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
        Cada plato o bebida se asigna a una estacion. Cuando el mozo manda un pedido, los items se
        agrupan automaticamente por estacion para que llegue a la ticketera correcta.
      </Typography>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'ID', minWidth: 60 },
          {
            id: 'type', label: 'Tipo',
            format: (value: StationType) => (
              <Chip
                icon={value === 'bar' ? <BarIcon /> : <KitchenIcon />}
                label={value === 'bar' ? 'Bar' : 'Cocina'}
                size="small"
                color={value === 'bar' ? 'info' : 'warning'}
              />
            ),
          },
          { id: 'name', label: 'Nombre', sx: { fontWeight: 500 } },
          {
            id: 'description', label: 'Descripcion',
            format: (v: string) => v || <span style={{ color: '#9CA3AF' }}>—</span>,
          },
          {
            id: 'printerIp', label: 'Ticketera (IP:Puerto)',
            format: (_v: any, row: Station) => row.printerIp
              ? `${row.printerIp}:${row.printerPort ?? 9100}`
              : <span style={{ color: '#9CA3AF' }}>Sin configurar</span>,
          },
          {
            id: 'isActive', label: 'Estado',
            format: (value: boolean) => (
              <Chip
                label={value ? 'Activa' : 'Inactiva'}
                size="small"
                color={value ? 'success' : 'default'}
              />
            ),
          },
        ]}
        rows={stations}
        searchFields={['name', 'type', 'description']}
        exportFileName="estaciones"
        emptyMessage="No hay estaciones registradas. Agrega al menos una 'Cocina' y un 'Bar' para asignar los platos y bebidas."
        renderActions={(s) => (
          <>
            <IconButton
              size="small"
              color="success"
              onClick={() => handleTestPrint(s)}
              title="Probar impresora"
              disabled={!s.printerIp}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(s)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(s.id); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Estacion' : 'Nueva Estacion'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Tipo"
                value={selected.type || 'cocina'}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <MenuItem value="cocina">🍳 Cocina</MenuItem>
                <MenuItem value="bar">🍺 Bar</MenuItem>
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Nombre"
                value={selected.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                placeholder="Ej: Cocina principal"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descripcion (opcional)"
                value={selected.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                multiline
                rows={2}
                placeholder="Ubicacion fisica o notas"
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Configuracion de ticketera (opcional, para integracion futura)
              </Typography>
            </Grid>
            <Grid size={7}>
              <TextField
                fullWidth
                label="IP de la ticketera"
                value={selected.printerIp || ''}
                onChange={(e) => handleChange('printerIp', e.target.value)}
                placeholder="192.168.1.50"
              />
            </Grid>
            <Grid size={5}>
              <TextField
                fullWidth
                label="Puerto"
                type="number"
                value={selected.printerPort || ''}
                onChange={(e) => handleChange('printerPort', e.target.value)}
                placeholder="9100"
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selected.isActive !== false}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="Estacion activa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!selected.name || !selected.type}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Estacion"
        message="¿Estas seguro? Los platos/bebidas asignados a esta estacion quedaran sin asignacion."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default Stations;
